"use client";

import * as React from "react";
import { getPortraitUrl } from "../../../wallet-info/constants";
import {
  formatGasLimit,
  formatTokenUnits,
  formatWeiToGwei,
  formatWeiToNative,
  getEstTxFeeWei,
  getGasMode,
  getGasPriceWei,
  isBscLegacyChainId,
} from "../../model/sendFormat";
import type { SendFlow } from "../../model/useSendFlow";
import { isNativeSendAsset } from "../../utils";
import { DownArrowIcon } from "../icons";

const PLACEHOLDER = "—";

export function SendReview({ flow }: { flow: SendFlow }) {
  const {
    token,
    reviewRawAmount,
    reviewRecipient,
    accountName,
    accounts,
    walletAddress,
    networkName,
    nativeSymbol,
    nativeDecimals,
    gasEstimate,
    gasLoading,
    gasError,
    insufficientGas,
    error,
    resolvedEnv,
  } = flow;

  const isNative = isNativeSendAsset(token);
  const amountDisplay =
    reviewRawAmount !== null
      ? formatTokenUnits(reviewRawAmount, token.quantity.decimals)
      : "";
  const fromName = accountName?.trim() || `Account ${(accounts[0]?.index ?? 0) + 1}`;
  const fromPortrait = getPortraitUrl(resolvedEnv, walletAddress, {
    size: 56,
    bg: "1A1A2E",
  });

  const displayNativeSymbol = nativeSymbol || (isNative ? token.symbol : "");
  const forceLegacyGas = isBscLegacyChainId(token.chainId);
  const gasMode = gasEstimate ? getGasMode(gasEstimate, forceLegacyGas) : null;
  const estTxFeeWei = gasEstimate
    ? getEstTxFeeWei(gasEstimate, forceLegacyGas)
    : null;
  const fee = (wei: bigint | null) =>
    wei !== null && displayNativeSymbol
      ? `${formatWeiToNative(wei, nativeDecimals)} ${displayNativeSymbol}`
      : PLACEHOLDER;
  const gasPrice = (wei: bigint | undefined) =>
    wei !== undefined ? `${formatWeiToGwei(wei)} Gwei` : PLACEHOLDER;

  const maxTotalText = (() => {
    if (!isNative || !gasEstimate || estTxFeeWei === null || reviewRawAmount === null)
      return PLACEHOLDER;
    const sum = reviewRawAmount + estTxFeeWei;
    return `${formatWeiToNative(sum, nativeDecimals)} ${displayNativeSymbol || token.symbol}`;
  })();

  const v = (text: string) => (gasLoading ? "…" : text);
  const gasRows = [
    { label: "Est. Tx Fee", value: v(fee(estTxFeeWei)) },
    ...(gasMode === "dynamic"
      ? [
          {
            label: "Max Priority Fee",
            value: v(gasPrice(gasEstimate?.maxPriorityFeePerGas)),
          },
          {
            label: "Max Gas Fee",
            value: v(gasPrice(gasEstimate?.maxFeePerGas)),
          },
        ]
      : gasMode === "legacy"
        ? [
            {
              label: "Gas Price",
              value: v(
                gasPrice(
                  gasEstimate ? getGasPriceWei(gasEstimate) : undefined,
                ),
              ),
            },
          ]
        : []),
    {
      label: "Gas Limit",
      value: v(gasEstimate ? formatGasLimit(gasEstimate.gasLimit) : PLACEHOLDER),
    },
  ];

  return (
    <div className="sf-body sf-review">
      <div className="sf-review-amount">
        <img
          className="sf-review-amount-icon"
          src={token.icon_url}
          alt=""
          width={40}
          height={40}
          draggable={false}
          onError={(e) => {
            e.currentTarget.src =
              "https://contents.crosstoken.io/wallet/token/images/FT.png";
          }}
        />
        <span className="sf-review-amount-value">
          {amountDisplay}
          <span className="sf-review-amount-symbol">{token.symbol}</span>
        </span>
      </div>

      <div className="sf-review-parties">
        <div className="sf-review-card sf-review-party">
          <span className="sf-review-party-label">From</span>
          <div className="sf-review-party-head">
            <span className="sf-review-party-avatar">
              {fromPortrait && (
                <img
                  src={fromPortrait}
                  alt=""
                  width={28}
                  height={28}
                  draggable={false}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
            </span>
            <span className="sf-review-party-name">{fromName}</span>
          </div>
          <span className="sf-review-party-addr">{walletAddress}</span>
        </div>
        <span className="sf-review-arrow" aria-hidden>
          <DownArrowIcon size={16} />
        </span>
        <div className="sf-review-card sf-review-party">
          <span className="sf-review-party-label">To</span>
          <span className="sf-review-party-addr">{reviewRecipient}</span>
        </div>
      </div>

      <section className="sf-review-card">
        <h3 className="sf-review-section-title">Summary</h3>
        <div className="sf-review-row">
          <span className="sf-review-row-label">Network</span>
          <span className="sf-review-row-value">{networkName}</span>
        </div>
        {gasRows.map((row) => (
          <div className="sf-review-row" key={row.label}>
            <span className="sf-review-row-label">{row.label}</span>
            <span className="sf-review-row-value">{row.value}</span>
          </div>
        ))}
      </section>

      <section className="sf-review-card">
        <h3 className="sf-review-section-title">Total</h3>
        <div className="sf-review-row">
          <span className="sf-review-row-label">Total Amount</span>
          <span className="sf-review-row-value">
            {amountDisplay} {token.symbol}
          </span>
        </div>
        {isNative && (
          <div className="sf-review-row">
            <span className="sf-review-row-label">Max. Total Amount</span>
            <span className="sf-review-row-value">{v(maxTotalText)}</span>
          </div>
        )}
      </section>

      {insufficientGas && !gasError && !error && (
        <div className="sf-message sf-message--error" role="alert">
          Not enough {displayNativeSymbol || token.symbol} to cover the amount
          plus the network fee. Reduce the amount and try again.
        </div>
      )}
      {(gasError || error) && (
        <div className="sf-message sf-message--error" role="alert">
          {gasError || error}
        </div>
      )}
    </div>
  );
}
